import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, Loader2, AlertTriangle } from 'lucide-react';
import { productService } from '../services/productService';
import { Product } from '../types/product';
import { clsx } from 'clsx';

interface ProductSearchProps {
  onProductSelect: (product: Product) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeProductIds?: number[];
  className?: string;
}

/**
 * Componente de busca de produtos com autocomplete
 * Permite buscar por SKU ou nome/descrição do produto
 */
export const ProductSearch: React.FC<ProductSearchProps> = ({
  onProductSelect,
  placeholder = "Buscar produto por SKU ou nome...",
  disabled = false,
  excludeProductIds = [],
  className
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<number>();

  // Buscar produtos com debounce
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setProducts([]);
      setLoading(false);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        // Busca produtos por nome ou SKU
        const response = await productService.searchProducts(
          query, 
          1, 
          10, 
          { active: true }
        );

        // Filtrar produtos excluídos
        const filteredProducts = response.products.filter(
          product => !excludeProductIds.includes(product.id)
        );

        setProducts(filteredProducts);
        setIsOpen(filteredProducts.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        setProducts([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [query, excludeProductIds]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < products.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < products.length) {
          handleProductSelect(products[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Scroll to selected item
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[selectedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleProductSelect = (product: Product) => {
    onProductSelect(product);
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleInputFocus = () => {
    if (products.length > 0) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay to allow click on items
    setTimeout(() => {
      setIsOpen(false);
      setSelectedIndex(-1);
    }, 150);
  };

  return (
    <div className={clsx('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            'w-full pl-10 pr-10 py-2 border border-slate-300 rounded-md',
            'focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:bg-slate-100 disabled:cursor-not-allowed',
            {
              'border-blue-300': isOpen,
            }
          )}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 animate-spin" size={18} />
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
          <ul ref={listRef} className="py-1">
            {products.length === 0 && !loading && (
              <li className="px-4 py-2 text-sm text-slate-500">
                Nenhum produto encontrado
              </li>
            )}
            
            {products.map((product, index) => (
              <li
                key={product.id}
                onClick={() => handleProductSelect(product)}
                className={clsx(
                  'px-4 py-2 cursor-pointer hover:bg-slate-50',
                  'flex items-center space-x-3',
                  {
                    'bg-blue-50 text-blue-700': index === selectedIndex,
                  }
                )}
              >
                <Package size={16} className="text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-sm text-slate-900">
                      {product.sku}
                    </span>
                    <span className="text-sm text-slate-500">-</span>
                    <span className="text-sm text-slate-700 truncate">
                      {product.description}
                    </span>
                  </div>
                  {product.description && (
                    <div className="text-xs text-slate-500 mt-1 truncate">
                      {product.description}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-medium text-slate-900">
                    R$ {product.price ? product.price.toFixed(2) : '0.00'}
                  </div>
                  {product.stock_quantity_available !== undefined && product.stock_quantity_available !== null ? (
                    <div className={clsx('text-xs font-medium', {
                      'text-red-600': product.stock_quantity_available < 0,
                      'text-amber-600': product.stock_quantity_available === 0,
                      'text-orange-600': product.stock_quantity_available > 0 && (product.min_stock_quantity || 0) > 0 && product.stock_quantity_available < (product.min_stock_quantity || 0),
                      'text-emerald-600': product.stock_quantity_available > 0 && (!product.min_stock_quantity || product.stock_quantity_available >= product.min_stock_quantity),
                    })}>
                      Est. Disponível: {product.stock_quantity_available}
                      {product.stock_quantity_available < 0 && <AlertTriangle size={12} className="inline ml-1" />}
                    </div>
                  ) : product.stock_quantity !== undefined ? (
                    <div className="text-xs text-slate-500">
                      Estoque: {product.stock_quantity}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};